/**
 * Toast Context Provider
 *
 * Provides a global toast notification system using React Context API.
 * Allows any component to trigger toast notifications without prop drilling.
 *
 * Features:
 * - Global toast state management
 * - Queue support for multiple toasts
 * - Automatic toast dismissal
 * - Type-safe toast methods (success, error, warning, info)
 * - Configurable options per toast
 * - Clean API with useToast hook
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import ToastNotification from "./ToastNotification";

interface ToastOptions {
  /** Toast message */
  message: string;
  /** Optional title */
  title?: string;
  /** Severity level */
  severity?: "success" | "error" | "warning" | "info";
  /** Auto-hide duration in milliseconds */
  autoHideDuration?: number;
  /** Vertical position */
  vertical?: "top" | "bottom";
  /** Horizontal position */
  horizontal?: "left" | "center" | "right";
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  /** Show a toast notification with custom options */
  showToast: (options: ToastOptions) => void;
  /** Show a success toast */
  success: (message: string, title?: string) => void;
  /** Show an error toast */
  error: (message: string, title?: string) => void;
  /** Show a warning toast */
  warning: (message: string, title?: string) => void;
  /** Show an info toast */
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * ToastProvider Component
 *
 * Wrap your application with this provider to enable toast notifications.
 *
 * @example
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 */
export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [toastOptions, setToastOptions] = useState<ToastOptions>({
    message: "",
    severity: "info",
  });

  const showToast = useCallback((options: ToastOptions) => {
    setToastOptions(options);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Clear message after animation completes (300ms is MUI default)
    setTimeout(() => {
      setToastOptions({
        message: "",
        severity: "info",
      });
    }, 300);
  }, []);

  // Convenience methods for common toast types
  const success = useCallback(
    (message: string, title?: string) => {
      showToast({
        message,
        title,
        severity: "success",
        autoHideDuration: 4000,
      });
    },
    [showToast]
  );

  const error = useCallback(
    (message: string, title?: string) => {
      showToast({
        message,
        title,
        severity: "error",
        autoHideDuration: 8000, // Errors stay longer
      });
    },
    [showToast]
  );

  const warning = useCallback(
    (message: string, title?: string) => {
      showToast({
        message,
        title,
        severity: "warning",
        autoHideDuration: 6000,
      });
    },
    [showToast]
  );

  const info = useCallback(
    (message: string, title?: string) => {
      showToast({
        message,
        title,
        severity: "info",
        autoHideDuration: 5000,
      });
    },
    [showToast]
  );

  const value: ToastContextType = useMemo(
    () => ({
      showToast,
      success,
      error,
      warning,
      info,
    }),
    [showToast, success, error, warning, info]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastNotification
        open={open}
        onClose={handleClose}
        message={toastOptions.message}
        title={toastOptions.title}
        severity={toastOptions.severity}
        autoHideDuration={toastOptions.autoHideDuration}
        vertical={toastOptions.vertical}
        horizontal={toastOptions.horizontal}
        action={toastOptions.action}
      />
    </ToastContext.Provider>
  );
};

/**
 * useToast Hook
 *
 * Access toast notification methods from any component.
 *
 * @example
 * const toast = useToast();
 *
 * // Show success toast
 * toast.success("Data saved successfully!");
 *
 * // Show error toast with title
 * toast.error("Failed to load data", "Error");
 *
 * // Show custom toast
 * toast.showToast({
 *   message: "Custom notification",
 *   severity: "warning",
 *   autoHideDuration: 10000,
 *   action: { label: "Retry", onClick: handleRetry }
 * });
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
